import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './InsufficientCreditsDialog.css';
import UpgradePromptDialog from './UpgradePromptDialog';
import { UPSELL_PROMPTS, resolveUnlockTier } from '../upsell/triggers';

// The server-side rejection path: generate_edited_video came back
// INSUFFICIENT_CREDITS, so the render never started.
//
// #350 EXTENDS this rather than adding a parallel prompt. Top-up ("Buy Credits")
// stays the primary remedy — it is the smallest thing that unblocks the render
// they were trying to make — with a second door to the subscription that stops
// them hitting this every month. That door opens the shared
// UpgradePromptDialog, so the tier it names and the funnel events it emits are
// identical to the other two touchpoints.
const InsufficientCreditsDialog = ({
  requiredCredits,
  balance,
  onCancel,
  onBuyCredits,
  // Effective entitlements + raw Stripe state for the upgrade path. Absent (or
  // on a top-tier account) the upgrade door is not offered and this behaves
  // exactly as it did before.
  entitlements,
  subscription,
  projectId,
}) => {
  const hasNumbers = Number.isFinite(requiredCredits) && Number.isFinite(balance);
  const shortBy = hasNumbers ? Math.max(0, requiredCredits - balance) : null;
  const [showUpgrade, setShowUpgrade] = useState(false);

  const monthlyCredits = (entitlements && entitlements.monthlyCredits) || 0;
  // Same load-window guard as the other two credit touchpoints: while the
  // users/{uid} snapshot is in flight every account reads as Free, so the door
  // would pitch a plan to a paying customer.
  const entitlementsLoading = Boolean(entitlements && entitlements.loading);
  // Only offer the subscription door when a tier actually adds monthly credits
  // on top of what they already get — a Pro account out of credits needs a
  // top-up, not a "plan" that would give them fewer.
  const canUpgrade = !entitlementsLoading && Boolean(
    resolveUnlockTier(UPSELL_PROMPTS.CREDITS_EXHAUSTED, { monthlyCredits }),
  );

  return (
    <div className="dialog-overlay" data-testid="insufficient-credits-dialog">
      <div className="dialog-content insufficient-credits-dialog">
        <h3>Not Enough Credits</h3>
        {hasNumbers ? (
          <>
            <p className="insufficient-credits-summary">
              This video needs <strong>{requiredCredits}</strong> {requiredCredits === 1 ? 'credit' : 'credits'},
              but you only have <strong>{balance}</strong>.
            </p>
            <p className="insufficient-credits-shortfall">
              You&rsquo;re short {shortBy} {shortBy === 1 ? 'credit' : 'credits'}. Top up to keep going.
            </p>
          </>
        ) : (
          <p className="insufficient-credits-summary">
            You don&rsquo;t have enough credits to generate this video.
          </p>
        )}
        <div className="dialog-buttons">
          <button onClick={onCancel} className="btn-secondary">Cancel</button>
          <Link
            to="/credits"
            className="btn btn-teal"
            onClick={onBuyCredits}
            data-testid="buy-credits-cta"
          >
            Buy Credits
          </Link>
        </div>
        {canUpgrade && (
          <p className="insufficient-credits-upsell">
            <button
              type="button"
              className="insufficient-credits-upsell__link"
              onClick={() => setShowUpgrade(true)}
              data-testid="insufficient-credits-upgrade"
            >
              Or get credits every month with a plan
            </button>
          </p>
        )}

        {showUpgrade && (
          <UpgradePromptDialog
            promptId={UPSELL_PROMPTS.CREDITS_EXHAUSTED}
            entitlements={entitlements}
            subscription={subscription}
            shortBy={shortBy}
            projectId={projectId}
            onTopUp={() => setShowUpgrade(false)}
            onClose={() => setShowUpgrade(false)}
          />
        )}
      </div>
    </div>
  );
};

export default InsufficientCreditsDialog;
